'use client'

import { Link, usePathname, useRouter } from '@/shared/navigation'
import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { delmanTerms } from '@/features/delman/delmanTerms'
import {
  getDelmanAttendanceStatus,
  loadDelmanAttendance,
  todayDateKey,
} from '@/features/delman/storage/delmanAttendanceStorage'
import { getOrCreateTodayTrip, type TripStatus } from '@/features/delman/storage/tripStorage'
import { clearDelmanSession } from '@/shared/auth/delmanSessionStorage'
import { getDelmanDevice } from '@/shared/auth/delmanDeviceStorage'
import { DelmanSyncStatusIndicator } from '@/shared/components/DelmanSyncStatusIndicator'
import { DelmanOfflineBanner } from '@/features/delman/components/DelmanOfflineBanner'
import { useKitchenTransferPending } from '@/features/delman/hooks/useKitchenTransferPending'
import { useClientHydrated } from '@/shared/hooks/useClientHydrated'

const nav = [
  { to: '/delman/trip', label: delmanTerms.modules.trip, icon: 'local_shipping' },
  {
    to: '/delman/kitchen-transfer',
    label: delmanTerms.modules.kitchenTransfer,
    icon: 'swap_horiz',
    showKitchenTransferBadge: true,
  },
  { to: '/delman/absensi', label: 'Absen', icon: 'face_retouching_natural', showAttendanceBadge: true },
  { to: '/delman/pickup-retur', label: 'Retur', icon: 'qr_code_scanner', showReturnBadge: true },
] as const

function tripStatusLabel(status: TripStatus): string {
  if (status === 'in_progress') return delmanTerms.trip.inProgress
  if (status === 'completed') return delmanTerms.trip.completed
  return delmanTerms.trip.ready
}

function tripStatusClass(status: TripStatus): string {
  if (status === 'in_progress') return 'bg-primary-container text-on-primary-container'
  if (status === 'completed') return 'badge-success'
  return 'badge-draft'
}

export function DelmanShell({ children }: { children: ReactNode }) {
  const hydrated = useClientHydrated()
  const device = hydrated ? getDelmanDevice() : null
  const router = useRouter()
  const pathname = usePathname()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const trip = device ? getOrCreateTodayTrip(device.delmanName, device.kitchenName) : null
  const pendingReturns =
    trip?.returnPickups.filter((r) => r.status === 'waiting_scan').length ?? 0
  const attendanceStatus = device
    ? getDelmanAttendanceStatus(loadDelmanAttendance(device.delmanId, todayDateKey()))
    : 'belum'
  const needsAttendance = attendanceStatus === 'belum'
  const deviceId = device?.deviceId ?? null
  const pendingKitchenTransfers = useKitchenTransferPending(Boolean(deviceId))

  useEffect(() => {
    if (!menuOpen) return undefined
    function onPointerDown(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [menuOpen])

  function handleLogout() {
    clearDelmanSession()
    router.replace('/delman/login')
  }

  return (
    <div className="min-h-screen flex flex-col max-w-md mx-auto bg-surface">
      <header className="sticky top-0 z-10 bg-inverse-surface text-inverse-on-surface px-4 h-14 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="material-symbols-outlined text-primary-container shrink-0">local_shipping</span>
          <div className="min-w-0">
            <p className="font-headline text-sm font-bold text-primary-container leading-tight">
              {delmanTerms.appName}
            </p>
            <p className="text-caption text-inverse-on-surface/60 truncate">
              {device ? device.delmanName : '—'}
              {trip && (
                <>
                  {' · '}
                  <span className={`inline-flex uppercase font-bold px-1.5 py-0.5 rounded text-[9px] ${tripStatusClass(trip.status)}`}>
                    {tripStatusLabel(trip.status)}
                  </span>
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <DelmanSyncStatusIndicator />
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-inverse-on-surface/80 hover:text-inverse-on-surface hover:bg-white/10"
              aria-label="Menu"
              aria-expanded={menuOpen}
            >
              <span className="material-symbols-outlined">more_vert</span>
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 w-44 rounded-lg border border-outline-variant bg-surface-card shadow-lg py-1 z-20">
                {trip && (
                  <p className="px-3 py-2 text-[10px] font-mono text-on-surface-variant border-b border-surface-variant truncate">
                    {trip.id}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false)
                    handleLogout()
                  }}
                  className="w-full text-left px-3 py-2.5 text-body-sm text-status-danger hover:bg-surface-container min-h-[44px]"
                >
                  Keluar
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <DelmanOfflineBanner />

      <main className="flex-1 p-4 pb-20">{children}</main>

      <nav className="fixed bottom-0 left-0 right-0 z-10 bg-surface-container-lowest border-t border-outline-variant safe-area-pb">
        <div className="max-w-md mx-auto flex justify-around px-2 py-1">
          {nav.map((item) => {
            const isActive = pathname === item.to || pathname.startsWith(`${item.to}/`)
            const returnBadge =
              'showReturnBadge' in item && item.showReturnBadge && pendingReturns > 0
                ? pendingReturns
                : 0
            const kitchenTransferBadge =
              'showKitchenTransferBadge' in item &&
              item.showKitchenTransferBadge &&
              pendingKitchenTransfers > 0
                ? pendingKitchenTransfers
                : 0
            const showAttendanceDot =
              'showAttendanceBadge' in item && item.showAttendanceBadge && needsAttendance
            return (
              <Link
                key={item.to}
                href={item.to}
                className={`relative flex flex-col items-center gap-0.5 px-3 py-2 rounded-lg text-caption min-w-[60px] min-h-[44px] justify-center transition-colors ${
                  isActive ? 'text-primary font-bold bg-primary-container/30' : 'text-on-surface-variant'
                }`}
              >
                <span className="material-symbols-outlined relative text-[22px]">
                  {item.icon}
                  {returnBadge > 0 && (
                    <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-status-warning text-[9px] font-bold text-white flex items-center justify-center">
                      {returnBadge}
                    </span>
                  )}
                  {kitchenTransferBadge > 0 && (
                    <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-primary text-[9px] font-bold text-on-primary flex items-center justify-center">
                      {kitchenTransferBadge}
                    </span>
                  )}
                  {showAttendanceDot && (
                    <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-status-warning border-2 border-white" />
                  )}
                </span>
                {item.label}
              </Link>
            )
          })}
        </div>
      </nav>
    </div>
  )
}
